import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Check, Sparkles, ShieldCheck, X, AlertTriangle, Mail, Phone, Eye, MousePointer,
    FileText, BellRing, Layers, Image as ImageIcon, BarChart3, Calendar, ChevronDown,
} from "lucide-react";
import axios from "axios";
import SiteNav from "../../components/brand/SiteNav";
import SiteFooter from "../../components/brand/SiteFooter";
import { SoubourMark } from "../../components/brand/SoubourMark";

const PROBLEMS = [
    { icon: FileText, title: "Spreadsheets everywhere", body: "Sponsor lists in three docs, none of them current." },
    { icon: BellRing, title: "Missed renewals", body: "Deals lapse because no one tracked the date." },
    { icon: ImageIcon, title: "Logos lost in inboxes", body: "Last year's banner artwork buried in a thread." },
    { icon: BarChart3, title: "Reporting? What reporting?", body: "Sponsors ask 'what did we get?' and you guess." },
    { icon: AlertTriangle, title: "Forgotten sponsors", body: "Mid-tier deals slip through the cracks for months." },
    { icon: Layers, title: "Commercial silos", body: "Three people, three views, zero shared truth." },
];

const OUTCOMES = [
    { eyebrow: "ONE PLACE", title: "Every sponsor, agreement, and renewal in one workspace.", body: "Stop hunting through inboxes and shared drives. Floodlight keeps the whole commercial side of your club organised, sponsor by sponsor, season by season." },
    { eyebrow: "DELIVERABLES", title: "Track logos, kits, banners, social posts, every promise made.", body: "Upload artwork once. Pin it to a sponsor. Tick deliverables off as you go. When a sponsor asks 'where's our logo?', you have an answer in three seconds." },
    { eyebrow: "PROOF", title: "Show sponsors exactly what exposure they got.", body: "Live KPIs from your fan app: visits, offer clicks, CTA taps. Renewal conversations stop being a sales pitch and start being a receipt." },
    { eyebrow: "RENEWALS", title: "Never miss a renewal opportunity again.", body: "Auto-flag deals 60 days before expiry. Quote, send, sign. Floodlight nudges you so the calendar doesn't have to." },
    { eyebrow: "INVENTORY", title: "Sell your ground like a stadium, not a Word doc.", body: "Your real ground, real stands, real boards, mapped and dragged onto a virtual pitch. Build a sponsor pack PDF in one click." },
];

const FAQS = [
    { q: "Is Floodlight just for big clubs?", a: "No, it's built for grassroots and semi-pro. The clubs running on goodwill and gut feel. The platform scales: same tool whether you've got 4 sponsors or 40." },
    { q: "Can it work with our existing fan app?", a: "Yes, if your fan app was built by Soubor Applications. We sync directly with your existing Firestore data so visits, offer clicks, and CTA taps appear inside Floodlight in real time. Third-party fan-app developers aren't supported at this time, get in touch and we'll talk." },
    { q: "What does it cost?", a: "We don't want Floodlight to be a burden or a worry. That's why we start on a small commission-based setup, no monthly subscription to renew, no surprise invoices. Floodlights stay on, ready for the club, and the small commission only kicks in when those sponsor contracts actually come in. It helps us keep the lights on so you can keep yours on too." },
    { q: "Does Floodlight replace our sponsor agreements / contracts?", a: "No, your contracts stay yours. Floodlight is the operating layer on top: who's signed, what they're owed, what's been delivered, and what's coming up." },
    { q: "Will sponsors see our brand or yours?", a: "Yours. Sponsors log in to a fully branded portal at floodlight.club/<yourclub>, your colours, your crest, your name. We sit quietly in the footer, just letting clubs know what's powering the experience. Floodlights stay on, illuminating opportunities for the club and proving exactly what sponsors are getting." },
    { q: "Who built this?", a: "Founders who grew up in non-league football and spent years watching clubs lose deals to admin chaos. We built the tool we wished clubs had." },
];

export default function Landing() {
    const [demoOpen, setDemoOpen] = useState(false);

    return (
        <div data-testid="landing-page" className="landing-scope min-h-screen bg-[var(--sb-page)] text-white">
            <SiteNav onBookDemo={() => setDemoOpen(true)} />
            <Hero onBookDemo={() => setDemoOpen(true)} />
            <TrustStrip />
            <ProblemSection />
            <OutcomesSection onBookDemo={() => setDemoOpen(true)} />
            <SponsorExpectations />
            <ScreenshotGallery />
            <BenefitsBanner />
            <CtaBanner onBookDemo={() => setDemoOpen(true)} />
            <FaqSection />
            <FinalCta onBookDemo={() => setDemoOpen(true)} />
            <SiteFooter />
            {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
        </div>
    );
}

/* ---------------- HERO ---------------- */
const Hero = ({ onBookDemo }) => (
    <section className="relative overflow-hidden border-b hairline" data-testid="landing-hero">
        <div aria-hidden className="absolute -top-40 right-[-15%] w-[700px] h-[700px] electric-glow" />
        <div aria-hidden className="absolute -bottom-40 left-[-10%] w-[600px] h-[600px] amber-glow" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-28 relative z-10">
            <div className="inline-flex items-center gap-2 border hairline px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-300 mb-10 reveal">
                <span className="kpi-pip" style={{ background: "var(--fl-electric)" }} />
                For grassroots & semi-pro football clubs
            </div>
            <h1 className="text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold leading-[1.02] text-balance max-w-[14ch] reveal reveal-d1" data-testid="hero-title">
                Sponsors are serious business. <span style={{ background: "linear-gradient(90deg, var(--sb-amber) 0%, var(--fl-electric-bright) 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Start managing them like one.</span>
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl mt-8 max-w-2xl leading-relaxed reveal reveal-d2" data-testid="hero-sub">
                Floodlight helps clubs manage sponsors, track agreements, organise assets, and prove value, all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-10 reveal reveal-d3">
                <button onClick={onBookDemo} data-testid="hero-book-demo"
                    className="group inline-flex items-center justify-center gap-2 bg-[var(--sb-amber)] text-black font-semibold px-7 py-4 hover:brightness-110 transition-all">
                    Book a demo
                    <ArrowRight size={16} strokeWidth={2.4} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <a href="#in-action" data-testid="hero-see-action"
                    className="inline-flex items-center justify-center gap-2 border hairline px-7 py-4 text-white hover:border-white/30 transition-all">
                    See it in action
                </a>
            </div>
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 max-w-3xl reveal reveal-d4">
                <HeroStat value="4×" label="Faster renewal cycle" />
                <HeroStat value="100%" label="Of deliverables tracked" />
                <HeroStat value="0" label="Spreadsheets needed" />
                <HeroStat value="1 click" label="Sponsor pack PDF" />
            </div>
        </div>
    </section>
);

const HeroStat = ({ value, label }) => (
    <div className="bg-[var(--sb-page)] p-5">
        <div className="font-landing-display text-3xl md:text-4xl font-bold tracking-tight">{value}</div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mt-2">{label}</div>
    </div>
);

/* ---------------- TRUST STRIP (dynamic from /api/public/landing/clubs) ---------------- */
const TrustStrip = () => {
    const [clubs, setClubs] = useState(null); // null = loading, [] = nothing to show

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get(`${base}/api/public/landing/clubs`);
                if (!cancelled) setClubs(Array.isArray(data?.clubs) ? data.clubs : []);
            } catch {
                if (!cancelled) setClubs([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Hide section entirely if no clubs are opted-in — we'd rather show nothing than fake logos.
    if (!clubs || clubs.length === 0) return null;

    return (
        <section className="border-b hairline py-10" data-testid="landing-trust">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 text-center mb-6">
                    Trusted by clubs building real sponsorship revenue
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-zinc-400">
                    {clubs.map((c) => (
                        <span
                            key={c.slug}
                            className="font-landing-display text-sm md:text-base font-medium tracking-tight uppercase inline-flex items-center gap-2"
                            data-testid={`trust-club-${c.slug}`}
                        >
                            <span
                                aria-hidden
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ background: c.brand?.primary || "var(--sb-amber)" }}
                            />
                            {c.name}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ---------------- PROBLEM ---------------- */
const ProblemSection = () => (
    <section className="border-b hairline py-24 md:py-32" data-testid="landing-problem">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl">
                <div className="text-[11px] uppercase tracking-[0.18em] mb-4" style={{ color: "var(--fl-electric-bright)" }}>The reality</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">Sponsorship management is messy.</h2>
                <p className="text-zinc-400 text-lg md:text-xl mt-6 leading-relaxed">
                    Most clubs run their commercial side on goodwill, post-it notes, and a WhatsApp group. It works, until it doesn't.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 mt-16">
                {PROBLEMS.map((p) => (
                    <div key={p.title} className="bg-[var(--sb-page)] p-7 md:p-8 hover:bg-white/[0.02] transition-colors">
                        <p.icon size={20} className="text-zinc-500 mb-5" strokeWidth={1.6} />
                        <div className="font-landing-display text-lg font-semibold tracking-tight">{p.title}</div>
                        <div className="text-zinc-500 text-sm mt-2 leading-relaxed">{p.body}</div>
                    </div>
                ))}
            </div>
            <div className="mt-16 max-w-2xl">
                <p className="text-2xl md:text-3xl font-medium leading-snug text-balance">
                    Floodlight fixes all of this. <span className="text-zinc-500">In one workspace, branded as your club.</span>
                </p>
            </div>
        </div>
    </section>
);

/* ---------------- OUTCOMES (features rewritten as outcomes) ---------------- */
const OutcomesSection = () => (
    <section id="how-it-works" className="border-b hairline py-24 md:py-32" data-testid="landing-outcomes">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl mb-20">
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">What you get</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">A proper system. Not another tool to learn.</h2>
            </div>
            <div className="space-y-px bg-white/5">
                {OUTCOMES.map((o, i) => (
                    <div key={o.title} className="bg-[var(--sb-page)] grid grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 px-1 md:px-2 group hover:bg-white/[0.015] transition-colors">
                        <div className="col-span-12 md:col-span-3">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                                <span style={{ color: "var(--sb-amber)" }}>{String(i + 1).padStart(2, "0")}</span> · {o.eyebrow}
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-9 max-w-3xl">
                            <h3 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight text-balance">{o.title}</h3>
                            <p className="text-zinc-400 text-base md:text-lg mt-4 leading-relaxed">{o.body}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------------- SPONSOR EXPECTATIONS ---------------- */
const SponsorExpectations = () => (
    <section className="relative border-b hairline py-24 md:py-32 overflow-hidden" data-testid="landing-expectations">
        <div aria-hidden className="absolute top-1/4 right-[-15%] w-[600px] h-[600px] electric-glow" style={{ opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-12 gap-10">
                <div className="col-span-12 lg:col-span-7">
                    <div className="text-[11px] uppercase tracking-[0.18em] mb-4" style={{ color: "var(--fl-electric-bright)" }}>The new bar</div>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">Sponsors don't just want a logo anymore.</h2>
                    <p className="text-zinc-400 text-lg md:text-xl mt-6 leading-relaxed max-w-2xl">
                        They want visibility, engagement, reporting, ongoing exposure, and proof. The clubs that deliver that win the renewal. The ones that don't lose to the pub down the road.
                    </p>
                </div>
                <div className="col-span-12 lg:col-span-5 flex flex-col gap-3">
                    <ExpectRow icon={Eye} label="Real visibility across perimeter, programme, app" />
                    <ExpectRow icon={MousePointer} label="Engagement they can feel: fans clicking, calling, scanning" />
                    <ExpectRow icon={BarChart3} label="Reporting in numbers, not anecdotes" />
                    <ExpectRow icon={Calendar} label="Year-round exposure, not just on matchdays" />
                    <ExpectRow icon={ShieldCheck} label="A renewal pitch that already wrote itself" highlight />
                </div>
            </div>
        </div>
    </section>
);

const ExpectRow = ({ icon: Icon, label, highlight }) => (
    <div className={`flex items-center gap-4 border hairline px-5 py-4 ${highlight ? "border-[var(--sb-amber)]/40 bg-[rgba(245,158,11,0.04)]" : ""}`}>
        <Icon size={16} className={highlight ? "text-[var(--sb-amber)]" : "text-zinc-400"} strokeWidth={1.8} />
        <span className="text-sm md:text-base">{label}</span>
    </div>
);

/* ---------------- SCREENSHOT GALLERY ("See it in action") ---------------- */
const ScreenshotGallery = () => (
    <section id="in-action" className="border-b hairline py-24 md:py-32" data-testid="landing-screenshots">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl mb-16">
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">See it in action</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">Floodlight, in the wild.</h2>
                <p className="text-zinc-400 text-lg md:text-xl mt-6 leading-relaxed">
                    A walk-through of every workspace your club gets — sponsor dashboard, branded portal, virtual pitch, and the one-click sponsor pack.
                </p>
            </div>
            <div className="grid grid-cols-12 gap-4">
                <ScreenSlot className="col-span-12 lg:col-span-7 aspect-[16/10]" eyebrow="Club dashboard" title="One workspace. Every sponsor." sub="Renewals, MRR, capacity sold, all in one view." />
                <ScreenSlot className="col-span-12 lg:col-span-5 aspect-[16/10]" eyebrow="Sponsor portal" title="Branded as your club." sub="Sponsors log in to their own KPI dashboard." accent />
                <ScreenSlot className="col-span-12 md:col-span-6 aspect-[16/11]" eyebrow="Virtual pitch" title="Sell your real ground." sub="Drag-drop sponsor allocation across stands and boards." />
                <ScreenSlot className="col-span-12 md:col-span-6 aspect-[16/11]" eyebrow="Sponsor pack" title="One-click PDF." sub="Branded sponsor brochure ready to send before kick-off." />
            </div>
        </div>
    </section>
);

const ScreenSlot = ({ className, eyebrow, title, sub, accent }) => (
    <div className={`relative ${className} border hairline overflow-hidden group hover:border-white/30 transition-colors`}>
        <div aria-hidden className="absolute inset-0" style={{
            background: accent
                ? `linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(245,158,11,0.05) 100%)`
                : `linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(99,102,241,0.04) 100%)`,
        }} />
        <div aria-hidden className="absolute inset-0 editorial-grid opacity-40" />
        <div className="absolute inset-0 flex flex-col justify-end p-7 md:p-8">
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: accent ? "var(--fl-electric-bright)" : "var(--sb-amber)" }}>{eyebrow}</div>
            <div className="font-landing-display text-2xl md:text-3xl font-semibold mt-2 tracking-tight">{title}</div>
            <div className="text-zinc-400 text-sm md:text-base mt-1.5">{sub}</div>
        </div>
        <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-600">
            <span className="kpi-pip" style={{ background: accent ? "var(--fl-electric-bright)" : "var(--sb-amber)" }} /> live preview
        </div>
    </div>
);

/* ---------------- BENEFITS BANNER ---------------- */
const BenefitsBanner = () => (
    <section className="border-b hairline py-20 md:py-24" data-testid="landing-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-12 gap-6 md:gap-10">
                <Benefit icon={ShieldCheck} title="Built by people who get clubs" body="Founders who grew up in non-league football. We've been in the room when the deal got signed on a serviette." />
                <Benefit icon={Sparkles} title="Premium, branded as you" body="Every sponsor sees your colours, your crest, your tagline. Floodlight stays out of the way." />
                <Benefit icon={Check} title="No CRM jargon" body="No 'pipelines' or 'segments'. Just clear, useful tools you'll actually open every Monday morning." />
            </div>
        </div>
    </section>
);

const Benefit = ({ icon: Icon, title, body }) => (
    <div className="col-span-12 md:col-span-4">
        <Icon size={22} className="text-[var(--sb-amber)] mb-5" strokeWidth={1.6} />
        <div className="font-landing-display text-xl md:text-2xl font-semibold tracking-tight">{title}</div>
        <div className="text-zinc-400 text-base mt-3 leading-relaxed">{body}</div>
    </div>
);

/* ---------------- CTA BANNER (mid-page, pricing) ---------------- */
const CtaBanner = ({ onBookDemo }) => (
    <section id="pricing" className="relative border-b hairline py-24 md:py-28 overflow-hidden" data-testid="landing-cta-banner">
        <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(99,102,241,0.10) 0%, transparent 70%)" }} />
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">Pricing without the worry</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance">No subscription. No surprises. Just commission when it counts.</h2>
            <p className="text-zinc-400 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
                Floodlight isn't another monthly invoice on the desk. We start on a small commission of sponsor revenue, so the platform stays on, ready when you need it. The lights only earn their keep when those contracts come in.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button onClick={onBookDemo} data-testid="cta-banner-book-demo"
                    className="inline-flex items-center justify-center gap-2 bg-[var(--sb-amber)] text-black font-semibold px-8 py-4 hover:brightness-110 transition-all">
                    Book your demo <ArrowRight size={16} strokeWidth={2.4} />
                </button>
                <span className="text-[12px] text-zinc-500 italic">Every club is different. Let's build the right setup for yours.</span>
            </div>
        </div>
    </section>
);

/* ---------------- FAQ ---------------- */
const FaqSection = () => {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" className="border-b hairline py-24 md:py-32" data-testid="landing-faq">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-4">FAQ</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance mb-14">Common questions, straight answers.</h2>
                <div className="space-y-px bg-white/5">
                    {FAQS.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={f.q} className="bg-[var(--sb-page)]">
                                <button onClick={() => setOpen(isOpen ? -1 : i)}
                                    className="w-full flex items-center justify-between gap-6 py-6 text-left hover:opacity-80 transition-opacity"
                                    data-testid={`faq-${i}`}>
                                    <span className="font-landing-display text-lg md:text-xl font-semibold tracking-tight">{f.q}</span>
                                    <ChevronDown size={18} className={`shrink-0 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                </button>
                                {isOpen && (
                                    <div className="text-zinc-400 text-base md:text-lg leading-relaxed pb-7 max-w-3xl">{f.a}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ---------------- FINAL CTA ---------------- */
const FinalCta = ({ onBookDemo }) => (
    <section className="py-28 md:py-40 relative overflow-hidden" data-testid="landing-final-cta">
        <div aria-hidden className="absolute inset-0 opacity-30" style={{
            background: "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(245,158,11,0.10) 0%, transparent 60%)",
        }} />
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
            <SoubourMark size={48} className="mx-auto mb-8 opacity-90" />
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] text-balance">Run sponsors like you run the team.</h2>
            <p className="text-zinc-400 text-lg md:text-xl mt-8 max-w-2xl mx-auto">
                Floodlight on. The commercial side of football, finally taken seriously.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-12 justify-center">
                <button onClick={onBookDemo} data-testid="final-cta-book-demo"
                    className="inline-flex items-center justify-center gap-2 bg-[var(--sb-amber)] text-black font-semibold px-8 py-4 hover:brightness-110 transition-all">
                    Book a demo <ArrowRight size={16} strokeWidth={2.4} />
                </button>
                <Link to="/login" data-testid="final-cta-signin"
                    className="inline-flex items-center justify-center gap-2 border hairline px-8 py-4 text-white hover:border-white/30 transition-all">
                    I already have an account
                </Link>
            </div>
        </div>
    </section>
);

/* ---------------- DEMO MODAL ---------------- */
const DemoModal = ({ onClose }) => {
    const overlayRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");
    const [draft, setDraft] = useState({ name: "", email: "", club: "", role: "", phone: "", notes: "" });

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            await axios.post(`${base}/api/leads/demo`, draft);
            setDone(true);
        } catch (e2) {
            setErr(e2.response?.data?.detail || "Something went wrong. Please email us at hello@floodlight.club.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            ref={overlayRef}
            onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4 py-10 overflow-y-auto"
            data-testid="demo-modal"
        >
            <div className="w-full max-w-xl bg-[#0a0a0a] border hairline relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white p-2" data-testid="demo-modal-close" aria-label="Close">
                    <X size={18} />
                </button>
                {done ? (
                    <div className="p-10 text-center">
                        <div className="w-12 h-12 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "rgba(245,158,11,0.15)" }}>
                            <Check size={22} className="text-[var(--sb-amber)]" strokeWidth={2.4} />
                        </div>
                        <h3 className="font-landing-display text-2xl md:text-3xl font-bold tracking-tight mb-3">Got it.</h3>
                        <p className="text-zinc-400 text-base leading-relaxed max-w-sm mx-auto">
                            We'll email you within one working day to find a time. In the meantime, kick the tyres on the demo.
                        </p>
                        <button onClick={onClose}
                            className="mt-8 inline-flex items-center gap-2 bg-[var(--sb-amber)] text-black font-semibold px-6 py-3 hover:brightness-110 transition-all"
                            data-testid="demo-modal-done-close">
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-8 md:p-10" data-testid="demo-form">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Book a demo</div>
                        <h3 className="font-landing-display text-2xl md:text-3xl font-bold tracking-tight mb-2">Tell us about your club.</h3>
                        <p className="text-zinc-400 text-sm mb-8">Takes 30 seconds. We'll come back within one working day.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DemoField label="Your name *">
                                <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Jane Doe"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="demo-name" />
                            </DemoField>
                            <DemoField label="Email *">
                                <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="jane@yourclub.co.uk"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="demo-email" />
                            </DemoField>
                            <DemoField label="Club *">
                                <input required value={draft.club} onChange={(e) => setDraft({ ...draft, club: e.target.value })} placeholder="Your club's name"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="demo-club" />
                            </DemoField>
                            <DemoField label="Role at the club">
                                <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Commercial Director"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="demo-role" />
                            </DemoField>
                            <DemoField label="Phone (optional)" className="md:col-span-2">
                                <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="07700 900000"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
                                    data-testid="demo-phone" />
                            </DemoField>
                            <DemoField label="Anything we should know?" className="md:col-span-2">
                                <textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Number of sponsors, league, current setup…"
                                    className="w-full bg-transparent border hairline px-3.5 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors resize-none"
                                    data-testid="demo-notes" />
                            </DemoField>
                        </div>
                        {err && (
                            <div className="mt-5 border border-rose-500/40 text-rose-300 text-xs px-3 py-2.5">{err}</div>
                        )}
                        <button type="submit" disabled={submitting}
                            className="w-full mt-7 inline-flex items-center justify-center gap-2 bg-[var(--sb-amber)] text-black font-semibold px-6 py-3.5 hover:brightness-110 transition-all disabled:opacity-50"
                            data-testid="demo-submit">
                            {submitting ? "Sending…" : <>Send demo request <ArrowRight size={16} strokeWidth={2.4} /></>}
                        </button>
                        <div className="mt-5 text-[11px] text-zinc-600 text-center">
                            Or email us directly at <a href="mailto:hello@floodlight.club" className="text-zinc-400 hover:text-white">hello@floodlight.club</a>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const DemoField = ({ label, children, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{label}</span>
        {children}
    </label>
);
